import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useState } from "react";
import { MoreHorizontal, Paperclip, PenLine, ReceiptText, Scissors, Tag, Truck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { usePermissao } from "@/lib/use-permissao";
import { codigoDoPedido } from "@/lib/codigo-do-pedido";
import { EntityFilesPanel } from "@/components/files/EntityFilesPanel";
import type { IncomingOrderRow } from "./production-types";
import { useImpressaoDoPedido } from "./use-impressao-do-pedido";

const motivo = "text-[11px] font-normal text-zinc-400 dark:text-muted-foreground";

/**
 * "⋯" do pedido na lista e nos cards de /lab/pedidos: imprimir (etiquetas e recibo), anexar arquivos
 * e, quando passado `onEditarServico`, editar serviço. Fica dentro de linha/card clicável — todo
 * clique e tecla aqui param, senão abririam o pedido junto (o React propaga até pelo portal).
 */
export function AcoesDoPedido({
  order,
  onEditarServico,
  className,
}: {
  order: IncomingOrderRow;
  onEditarServico?: () => void;
  className?: string;
}) {
  const imp = useImpressaoDoPedido(order);
  const podeAnexar = usePermissao("files.manage");
  const [anexos, setAnexos] = useState(false);
  const parar = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div onClick={parar} onKeyDown={parar} className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Mais ações do pedido"
            title="Mais ações"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-muted"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60 border border-zinc-200 bg-white shadow-lg dark:border-border dark:bg-card">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-zinc-400">Imprimir</DropdownMenuLabel>
          <DropdownMenuItem onSelect={imp.etiquetaUnica} className="gap-2">
            <Scissors className="h-4 w-4 text-zinc-500" />
            Etiquetas únicas ({imp.quantidade})
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={imp.producao} className="gap-2">
            <Tag className="h-4 w-4 text-zinc-500" />
            Etiquetas de produção ({imp.quantidade})
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={imp.etiquetaDeEntrega} disabled={!imp.entrega} className="items-start gap-2">
            <Truck className="mt-0.5 h-4 w-4 text-zinc-500" />
            <span className="flex flex-col">
              Etiqueta de entrega
              {!imp.entrega && <span className={motivo}>Pedido sem endereço de envio</span>}
            </span>
          </DropdownMenuItem>
          {imp.podeVerPreco && (
            <DropdownMenuItem onSelect={imp.recibo} disabled={!imp.temValor} className="items-start gap-2">
              <ReceiptText className="mt-0.5 h-4 w-4 text-zinc-500" />
              <span className="flex flex-col">
                Recibo
                {!imp.temValor && <span className={motivo}>Nenhum serviço com valor</span>}
              </span>
            </DropdownMenuItem>
          )}
          {(podeAnexar || onEditarServico) && <DropdownMenuSeparator />}
          {podeAnexar && (
            <DropdownMenuItem onSelect={() => setAnexos(true)} className="gap-2">
              <Paperclip className="h-4 w-4 text-zinc-500" />
              Anexar arquivos
            </DropdownMenuItem>
          )}
          {onEditarServico && (
            <DropdownMenuItem onSelect={onEditarServico} className="gap-2">
              <PenLine className="h-4 w-4 text-amber-600" />
              Editar serviço
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={anexos} onOpenChange={setAnexos}>
        <DialogContent className="max-w-3xl border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
          <DialogHeader>
            <DialogTitle ajuda={AJUDA_PRODUCAO.arquivosDoPedido}>Arquivos do pedido #{codigoDoPedido(order)}</DialogTitle>
            <DialogDescription>
              {order.patientName} · fotos, escaneamentos (STL/PLY) e documentos ficam ligados ao pedido.
            </DialogDescription>
          </DialogHeader>
          <EntityFilesPanel relatedType="PROSTHESIS_ORDER" relatedId={order.id} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
